import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { AuthSession, Device, OtpChallenge, User } from '../../types/user';
import { OTP_ERROR_MESSAGES, createOtpChallenge, verifyOtpChallenge } from '../../core/otp';
import {
  clearSession,
  findUserByPhone,
  getDevice,
  hasLocalPin,
  refreshSession,
  registerDevice,
  restoreSession,
  seedMockUsers,
  setLocalPin,
  startSession,
  verifyLocalPin,
} from '../../db/auth-store';
import { cloudLogin, cloudLogout } from '../../db/cloud-auth-store';
import { SIGN_IN_REFUSALS, signInRefusalMessage } from '../../core/operator-access';
import { PhoneEntryStep } from './PhoneEntryStep';
import { OtpVerifyStep } from './OtpVerifyStep';
import { PinSetupStep } from './PinSetupStep';
import { PendingApprovalView } from './PendingApprovalView';

export interface SignedIn {
  user: User;
  session: AuthSession;
}

interface AuthFlowProps {
  onSignedIn: (result: SignedIn) => void;
}

type Step = 'boot' | 'phone' | 'otp' | 'pin-setup' | 'pin-unlock' | 'pending';

/**
 * Authentication state machine, with the centralised sign-in in front of it:
 *
 * ```
 *   phone + group password ──▶ /api/login ──┬─ accepted ──▶ signed in
 *                                           ├─ refused  ──▶ the server's reason
 *                                           └─ no answer ─▶ OTP → device gate → PIN
 * ```
 *
 * The cloud path is the one the unit uses: the D1 directory says whether the
 * number is allowed and at what level, the device is trusted on the strength of
 * that answer, and there is nothing else to tap. A refusal is *believed* — being
 * able to talk past "this number is not registered" is the one thing the
 * directory exists to stop.
 *
 * No answer at all is a different thing entirely, and is the whole reason the
 * old flow is still here: a phone in a dead spot falls through to the local OTP
 * and PIN it always had, against its own IndexedDB, and the application keeps
 * working exactly as it did before there was a directory.
 *
 * Both paths pass the same gate. The system is open to two roles — מנהל על and
 * מורשה / מפקד — and to nothing else: a נהג's number is refused at the first
 * screen, before a device is registered and before a code is issued, and would
 * be refused again by `startSession()` if it somehow got that far. Drivers do
 * not sign in; they receive their פקודת משימה as a shared image.
 *
 * On boot the stored session is rehydrated first, before anything decides what
 * to render. A reload or a mobile pull-to-refresh therefore lands back inside
 * the app; the PIN screen only appears on a cold launch — after the unlock
 * window has closed, or after the operator locked the app themselves. Whether
 * that restored session is still *allowed* is asked separately and in the
 * background, by `revalidateCloudSession()` in the shell, so a cold start with
 * no signal is never held up waiting for an answer that is not coming.
 */
export function AuthFlow({ onSignedIn }: AuthFlowProps) {
  const [step, setStep] = useState<Step>('boot');
  const [user, setUser] = useState<User | null>(null);
  const [device, setDevice] = useState<Device | null>(null);
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const signedInRef = useRef(onSignedIn);
  signedInRef.current = onSignedIn;
  const bootedRef = useRef(false);

  const resetToPhone = useCallback(() => {
    setUser(null);
    setDevice(null);
    setChallenge(null);
    setError(null);
    setBusy(false);
    setStep('phone');
  }, []);

  // ── Bootstrap: seed accounts and restore an existing session ────────
  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;

    (async () => {
      await seedMockUsers();
      const restored = await restoreSession();
      if (!restored) {
        setStep('phone');
        return;
      }

      setUser(restored.user);
      // Straight back in — either the unlock is still live (a reload or a
      // pull-to-refresh), or this installation has no PIN to ask for.
      if (restored.resumable || !(await hasLocalPin(restored.user.id))) {
        const session = (await refreshSession(restored.session.unlockMethod)) ?? restored.session;
        signedInRef.current({ user: restored.user, session });
        return;
      }
      setStep('pin-unlock');
    })().catch(() => setStep('phone'));
  }, []);

  // ── Step 1: phone + the group's shared password ────────────────────

  /**
   * The local flow — what a device with no signal falls back to.
   *
   * The operator gate is asked *first*, before a device is registered and before
   * a challenge is issued: a נהג's number must not receive a code for a door
   * that is not going to open. The rule itself is `signInRefusalMessage()` in
   * `src/core/operator-access.ts`, which is the same rule `startSession()`
   * enforces at the point the session would be written.
   */
  const continueLocally = async (phone: string) => {
    const found = await findUserByPhone(phone);
    const refusal = signInRefusalMessage(found);
    if (refusal || !found) {
      setError(refusal ?? SIGN_IN_REFUSALS.unknown);
      return;
    }
    const registration = await registerDevice(found);
    setUser(found);
    setDevice(registration.device);
    setChallenge(createOtpChallenge(found.phone));
    setStep('otp');
  };

  const handlePhoneSubmit = async (phone: string, password: string) => {
    setBusy(true);
    setError(null);
    try {
      const outcome = await cloudLogin(phone, password);

      if (outcome.kind === 'rejected') {
        setError(outcome.message);
        return;
      }

      if (outcome.kind === 'signed-in') {
        // The directory holds two roles and both are operators, so this branch
        // is expected to pass the gate — it is still asked, because "expected
        // to" is not the same as "cannot be otherwise".
        const refusal = signInRefusalMessage(outcome.user);
        if (refusal) {
          setError(refusal);
          return;
        }
        // The directory answered, so the device gate has nothing left to decide
        // and the OTP has nothing left to prove: one screen, and they are in.
        const registration = await registerDevice(outcome.user, { trusted: true });
        const session = await startSession(outcome.user, registration.device, 'password');
        signedInRef.current({ user: outcome.user, session });
        return;
      }

      await continueLocally(phone);
    } finally {
      setBusy(false);
    }
  };

  // ── Step 2: OTP ────────────────────────────────────────────────────
  const continueAfterOtp = async (verifiedUser: User, verifiedDevice: Device) => {
    // Re-asked here rather than trusted from step one: an account may have been
    // narrowed while the code was being typed, and this is the last point before
    // a session exists.
    const refusal = signInRefusalMessage(verifiedUser);
    if (refusal) {
      setError(refusal);
      setStep('phone');
      return;
    }
    if (!verifiedDevice.isApproved) {
      setError(null);
      setStep('pending');
      return;
    }
    if (!(await hasLocalPin(verifiedUser.id))) {
      setError(null);
      setStep('pin-setup');
      return;
    }
    const session = await startSession(verifiedUser, verifiedDevice, 'otp');
    signedInRef.current({ user: verifiedUser, session });
  };

  const handleVerifyOtp = async (code: string) => {
    if (!challenge || !user || !device) return;
    setBusy(true);
    try {
      const result = verifyOtpChallenge(challenge, code);
      if (!result.ok) {
        setChallenge(result.challenge);
        setError(OTP_ERROR_MESSAGES[result.reason]);
        return;
      }
      await continueAfterOtp(user, device);
    } finally {
      setBusy(false);
    }
  };

  const handleResendOtp = () => {
    if (!user) return;
    setError(null);
    setChallenge(createOtpChallenge(user.phone));
  };

  // ── Step 3: PIN ────────────────────────────────────────────────────
  const handlePinSetup = async (pin: string) => {
    if (!user || !device) return;
    setBusy(true);
    setError(null);
    try {
      await setLocalPin(user.id, pin);
      const session = await startSession(user, device, 'pin');
      signedInRef.current({ user, session });
    } catch {
      setError('שמירת הקוד נכשלה — נסו שוב');
    } finally {
      setBusy(false);
    }
  };

  const handlePinUnlock = async (pin: string) => {
    if (!user) return;
    setBusy(true);
    setError(null);
    try {
      const ok = await verifyLocalPin(user.id, pin);
      if (!ok) {
        setError('קוד שגוי');
        return;
      }
      const session = await refreshSession('pin');
      if (!session) {
        resetToPhone();
        return;
      }
      signedInRef.current({ user, session });
    } finally {
      setBusy(false);
    }
  };

  const handleSignOut = async () => {
    // Both halves of the session go: the local record, and the cloud token that
    // would otherwise let the next launch straight back past this screen.
    await Promise.all([clearSession(), cloudLogout()]);
    resetToPhone();
  };

  // ── Pending device approval ────────────────────────────────────────
  const handleRecheckApproval = async () => {
    if (!user || !device) return;
    setBusy(true);
    try {
      const fresh = await getDevice(device.id);
      if (!fresh) {
        resetToPhone();
        return;
      }
      setDevice(fresh);
      if (fresh.isApproved) await continueAfterOtp(user, fresh);
    } finally {
      setBusy(false);
    }
  };

  if (step === 'boot') {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-brand-500" size={28} aria-label="טוען" />
      </main>
    );
  }

  if (step === 'otp' && challenge) {
    return (
      <OtpVerifyStep
        challenge={challenge}
        onVerify={handleVerifyOtp}
        onResend={handleResendOtp}
        onBack={resetToPhone}
        busy={busy}
        error={error}
      />
    );
  }

  if (step === 'pending' && user && device) {
    return (
      <PendingApprovalView
        user={user}
        device={device}
        onRecheck={handleRecheckApproval}
        onCancel={resetToPhone}
        busy={busy}
      />
    );
  }

  if (step === 'pin-setup' && user) {
    return (
      <PinSetupStep
        mode="setup"
        fullName={user.fullName}
        onSubmit={handlePinSetup}
        onCancel={resetToPhone}
        busy={busy}
        error={error}
      />
    );
  }

  if (step === 'pin-unlock' && user) {
    return (
      <PinSetupStep
        mode="unlock"
        fullName={user.fullName}
        onSubmit={handlePinUnlock}
        onCancel={handleSignOut}
        busy={busy}
        error={error}
      />
    );
  }

  return <PhoneEntryStep onSubmit={handlePhoneSubmit} busy={busy} error={error} />;
}
